import type { ReactElement } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
    Boxes,
    CalendarDays,
    Carrot,
    CreditCard,
    DollarSign,
    FileClock,
    FileText,
    Flag,
    FolderTree,
    GraduationCap,
    LayoutDashboard,
    ListTree,
    LogOut,
    MapPin,
    MessageSquare,
    Network,
    Receipt,
    ScrollText,
    Search,
    Shield,
    Soup,
    Store,
    UserCheck,
    Utensils,
    Video
} from 'lucide-react';

import { useLogoutMutation } from '@/api/authApi';
import { useAuth } from '@/hooks/useAuth';

interface NavItem {
    to: string;
    label: string;
    icon: ReactElement;
}

export function Layout(): ReactElement {
    const { admin } = useAuth();
    const navigate = useNavigate();
    const [logoutMutation, { isLoading: isLoggingOut }] = useLogoutMutation();

    // JSX внутри функции-компонента, чтобы не триггерить eslint
    // react/no-multi-comp на stateless wrappers.
    const navItems: NavItem[] = [
        { to: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={16} /> },
        { to: '/moderation', label: 'Модерация', icon: <Shield size={16} /> },
        { to: '/recipes', label: 'Рецепты', icon: <Utensils size={16} /> },
        { to: '/articles', label: 'Статьи', icon: <FileText size={16} /> },
        { to: '/courses', label: 'Курсы', icon: <GraduationCap size={16} /> },
        { to: '/streams', label: 'Стримы', icon: <Video size={16} /> },
        { to: '/events', label: 'Мероприятия', icon: <CalendarDays size={16} /> },
        { to: '/comments', label: 'Комментарии', icon: <MessageSquare size={16} /> },
        { to: '/categories', label: 'Категории', icon: <FolderTree size={16} /> },
        {
            to: '/ingredient-groups',
            label: 'Группы ингредиентов',
            icon: <Boxes size={16} />
        },
        { to: '/ingredients', label: 'Ингредиенты', icon: <Carrot size={16} /> },
        { to: '/cuisine-types', label: 'Типы кухонь', icon: <Soup size={16} /> },
        { to: '/cities', label: 'Города', icon: <MapPin size={16} /> },
        { to: '/tariffs', label: 'Тарифы', icon: <CreditCard size={16} /> },
        { to: '/subscriptions', label: 'Подписки', icon: <Receipt size={16} /> },
        { to: '/payments', label: 'Платежи', icon: <DollarSign size={16} /> },
        {
            to: '/professional-applications',
            label: 'Заявки профессионалов',
            icon: <UserCheck size={16} />
        },
        { to: '/cafe-applications', label: 'Заявки кафе', icon: <Store size={16} /> },
        {
            to: '/vacancy-complaints',
            label: 'Жалобы на вакансии',
            icon: <Flag size={16} />
        },
        { to: '/specializations', label: 'Специализации', icon: <Network size={16} /> },
        {
            to: '/menu-item-types',
            label: 'Дерево типов меню',
            icon: <ListTree size={16} />
        },
        {
            to: '/pii-consent-texts',
            label: 'Согласия на ПД',
            icon: <ScrollText size={16} />
        },
        {
            to: '/cafe-staff-search-logs',
            label: 'Поиск кафе (лог)',
            icon: <Search size={16} />
        },
        { to: '/audit-log', label: 'Журнал действий', icon: <FileClock size={16} /> }
    ];

    const handleLogout = async (): Promise<void> => {
        try {
            await logoutMutation().unwrap();
        } finally {
            navigate('/login', { replace: true });
        }
    };

    return (
        <div className="flex min-h-screen bg-gray-50">
            <aside
                className="flex w-60 flex-col border-r border-gray-200 bg-white"
                data-testid="sidebar"
            >
                <div className="flex h-14 items-center border-b border-gray-200 px-4 text-lg font-semibold">
                    Cook-space Admin
                </div>
                <nav className="flex-1 space-y-1 p-3">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={`nav-${item.to.replace('/', '')}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 rounded px-3 py-2 text-sm transition ${
                                    isActive
                                        ? 'bg-gray-900 text-white'
                                        : 'text-gray-700 hover:bg-gray-100'
                                }`
                            }
                        >
                            {item.icon}
                            {item.label}
                        </NavLink>
                    ))}
                </nav>
            </aside>

            <div className="flex flex-1 flex-col">
                <header
                    className="flex h-14 items-center justify-between border-b border-gray-200 bg-white px-6"
                    data-testid="header"
                >
                    <div className="text-sm text-gray-600">
                        {admin?.displayName ?? admin?.email ?? '—'}
                    </div>
                    <button
                        type="button"
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        data-testid="logout-button"
                        className="flex items-center gap-2 rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition hover:bg-gray-100 disabled:opacity-50"
                    >
                        <LogOut size={14} />
                        Выйти
                    </button>
                </header>

                <main className="flex-1 p-6">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
